/**
 * `BrandMark` — the FastFrame mark, and the only component that draws it.
 *
 * It lives in `@tala/design-tokens` rather than in `apps/web/src/components`
 * because `apps/admin` is a separate Vite app whose own eslint config forbids
 * importing web source ("Share design tokens through @tala/design-tokens;
 * everything else is a fork, not a reuse"). A mark that the admin console
 * cannot import is a mark that gets copied, and a copied mark is what this
 * whole change exists to end. Both apps import THIS file; nothing anywhere
 * holds a second copy of the geometry.
 *
 * ONE FILE, BOTH THEMES. The brand kit ships black and white cuts of every
 * asset because an `<img>` cannot inherit a colour. Drawn inline, the mark
 * takes `currentColor`, so the theme is chosen by a token on the element — the
 * shell uses `text-fg-primary`, which is `#0F1011` in light and white in dark,
 * i.e. exactly the two cuts, without either file. That is also why the two
 * `ff-mark-grid-*.svg` exports were deleted: they were the path data's second
 * and third homes.
 *
 * ACCESSIBILITY. Default is decorative (`aria-hidden`), because the mark almost
 * always sits inside something already labelled — the "FastFrame home" link in
 * the shell, the assistant's own dock button. Pass `title` where the mark IS
 * the label (the sign-in lockup, the admin header) and it renders a `<title>`
 * with `role="img"` instead.
 */
import type { CSSProperties } from 'react';

import {
  BRAND_MARK_RADIUS,
  BRAND_MARK_SQUARE,
  BRAND_MARK_SQUARES,
  BRAND_MARK_VIEW_BOX,
} from './mark-geometry';
import {
  BRAND_LOCKUP_MARK_TRANSFORM,
  BRAND_LOCKUP_VIEW_BOX,
  BRAND_WORDMARK_PATHS,
} from './wordmark-paths';

/**
 * The sizes the product actually uses, named so a caller picks a step instead
 * of a number. `md` (28) is the shell's own glyph — the Assets frames draw the
 * mark at 28px inside a 40px box.
 */
export const BRAND_MARK_SIZES = {
  xs: 16,
  sm: 20,
  md: 28,
  lg: 40,
  xl: 64,
} as const;

export type BrandMarkSize = keyof typeof BRAND_MARK_SIZES;

export interface BrandMarkProps {
  /** A step off `BRAND_MARK_SIZES`. Omit and size it with a class instead. */
  size?: BrandMarkSize;
  /** Sizing / colour classes. Colour reaches the glyph through `currentColor`. */
  className?: string;
  style?: CSSProperties;
  /** Accessible name. Omitted (the default) the mark is decorative. */
  title?: string;
  /** Escape hatch for tests and snapshots. */
  'data-testid'?: string;
}

function labelling(title: string | undefined) {
  return title ? ({ role: 'img' as const } as const) : ({ 'aria-hidden': true } as const);
}

export function BrandMark({ size, className, style, title, ...rest }: BrandMarkProps) {
  const px = size ? BRAND_MARK_SIZES[size] : undefined;
  return (
    <svg
      viewBox={BRAND_MARK_VIEW_BOX}
      width={px}
      height={px}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      {...labelling(title)}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {BRAND_MARK_SQUARES.map(({ x, y }) => (
        <rect
          key={`${x}:${y}`}
          x={x}
          y={y}
          width={BRAND_MARK_SQUARE}
          height={BRAND_MARK_SQUARE}
          rx={BRAND_MARK_RADIUS}
        />
      ))}
    </svg>
  );
}

export interface BrandLockupProps extends Omit<BrandMarkProps, 'size'> {
  /** Sizing classes; the lockup keeps the kit's aspect ratio. */
  className?: string;
}

/**
 * Mark + "fastframe" wordmark, in the brand kit's own lockup metrics — the
 * thing that belongs on sign-in, the invite screen and the expanded sidebar.
 * It is composed, not exported: the kit's lockup file still welds the RETIRED
 * pinwheel to the wordmark, so drawing it would put the old mark back on the
 * first screen anyone sees.
 */
export function BrandLockup({ className, style, title, ...rest }: BrandLockupProps) {
  return (
    <svg
      viewBox={BRAND_LOCKUP_VIEW_BOX}
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      {...labelling(title)}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g transform={BRAND_LOCKUP_MARK_TRANSFORM}>
        {BRAND_MARK_SQUARES.map(({ x, y }) => (
          <rect
            key={`${x}:${y}`}
            x={x}
            y={y}
            width={BRAND_MARK_SQUARE}
            height={BRAND_MARK_SQUARE}
            rx={BRAND_MARK_RADIUS}
          />
        ))}
      </g>
      {BRAND_WORDMARK_PATHS.map((d) => (
        <path key={d.slice(0, 24)} fillRule="evenodd" clipRule="evenodd" d={d} />
      ))}
    </svg>
  );
}
